import { useCallback, useMemo } from 'react';
import { type Permission, type RoleSlug, canAssignRoleByHierarchy, canManageRoleByHierarchy, canManageUserByHierarchy, hasFullSystemAccess, userHierarchyLevel } from '@lms/shared-types';
import { filterVisibleTabs } from '@/config/siteAdminNavigation';
import { useAuthStore } from '@/stores/authStore';

export function usePermissions() {
  const user = useAuthStore((s) => s.user);

  const hasPermission = useCallback(
    (permission: Permission) => {
      if (user?.roles && hasFullSystemAccess(user.roles)) return true;
      return user?.permissions.includes(permission) ?? false;
    },
    [user],
  );

  const hasAnyPermission = useCallback(
    (...permissions: Permission[]) => permissions.some((p) => hasPermission(p)),
    [hasPermission],
  );

  const hasRole = useCallback(
    (role: RoleSlug) => user?.roles.includes(role) ?? false,
    [user],
  );

  const canManageRole = useCallback(
    (roleName: string, storedHierarchyLevel?: number | null) => {
      if (!user) return false;
      return canManageRoleByHierarchy(
        user.roles,
        { name: roleName, hierarchyLevel: storedHierarchyLevel },
        user.hierarchyLevel ?? userHierarchyLevel(user.roles),
      );
    },
    [user],
  );

  const canManageUser = useCallback(
    (targetRoleNames: readonly string[]) => {
      if (!user) return false;
      return canManageUserByHierarchy(user.roles, targetRoleNames, {
        actorLevel: user.hierarchyLevel ?? userHierarchyLevel(user.roles),
      });
    },
    [user],
  );

  const canAssignRole = useCallback(
    (roleName: string, storedHierarchyLevel?: number | null) => {
      if (!user) return false;
      return canAssignRoleByHierarchy(
        user.roles,
        { name: roleName, hierarchyLevel: storedHierarchyLevel },
        user.hierarchyLevel ?? userHierarchyLevel(user.roles),
      );
    },
    [user],
  );

  const hierarchyLevel = useMemo(
    () => user?.hierarchyLevel ?? (user ? userHierarchyLevel(user.roles) : null),
    [user],
  );

  const adminTabOptions = useMemo(
    () => ({ hasRole }),
    [hasRole],
  );

  const canAccessAdmin = useMemo(
    () => Object.keys(filterVisibleTabs(hasPermission, adminTabOptions)).length > 0,
    [hasPermission, adminTabOptions],
  );

  return { user, hasPermission, hasAnyPermission, hasRole, canManageRole, canAssignRole, canManageUser, hierarchyLevel, canAccessAdmin, adminTabOptions };
}
